import {
  type ConversationId,
  type ConversationThreadId,
  type DraftMessageId,
  DraftMessageId as DraftMessageIdSchema,
  type MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  type MongoUserId,
  MongoUserId as MongoUserIdSchema,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { toast } from "@redotech/redo-web/alert";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { DateTime } from "effect";
import { useCallback, useContext, useState } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

interface UseDraftOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
  enabled?: boolean;
}

/**
 * Hook to fetch the current draft for a conversation thread context.
 * Uses the TanStack DB drafts collection instead of React Query directly.
 */
export function useDraft({
  conversationId,
  threadId,
  inReplyToMessageId,
}: UseDraftOptions) {
  const collections = useGlobalConversationCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          inReplyToMessageId
            ? and(
                eq(draft.conversationId, String(conversationId)),
                eq(draft.threadId, String(threadId)),
                eq(draft.inReplyToMessageId, String(inReplyToMessageId)),
              )
            : and(
                eq(draft.conversationId, String(conversationId)),
                eq(draft.threadId, String(threadId)),
                isUndefined(draft.inReplyToMessageId),
              ),
        )
        .findOne(),
    [conversationId, threadId, inReplyToMessageId],
  );

  return { data: result.data ?? null };
}

interface UseDraftMutationsOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  inReplyToMessageId?: MessageId;
  existingDraftId?: DraftMessageId | null;
}

/**
 * Builds a temp DraftCollectionItem for non-optimistic inserts.
 * The item is never displayed — it only carries fields needed by onInsert
 * to make the saveDraft API call. Placeholder values are used for fields
 * that only exist on server-returned drafts (id, timestamps).
 */
function buildTempDraftItem(
  conversationId: ConversationId,
  threadId: ConversationThreadId,
  senderMongoUserId: MongoUserId,
  inReplyToMessageId: MessageId | undefined,
  overrides: Partial<
    Pick<
      DraftCollectionItem,
      "content" | "attachments" | "channel" | "channelData"
    >
  >,
): DraftCollectionItem {
  const now = DateTime.unsafeMake(new Date());
  return {
    id: DraftMessageIdSchema.generate(),
    conversationId,
    threadId,
    inReplyToMessageId: inReplyToMessageId
      ? String(inReplyToMessageId)
      : undefined,
    content:
      overrides.content ??
      JSON.stringify([{ type: "p", children: [{ text: "" }] }]),
    attachments: overrides.attachments ?? [],
    senderMongoUserId,
    channel: overrides.channel ?? "redoChat",
    channelData: overrides.channelData ?? null,
    status: "PENDING",
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Hook to manage draft mutations using the TanStack DB collection's
 * onInsert / onUpdate / onDelete callbacks (defined in collections.ts).
 */
export function useDraftMutations({
  conversationId,
  threadId,
  inReplyToMessageId,
  existingDraftId,
}: UseDraftMutationsOptions) {
  const collections = useGlobalConversationCollections();
  const user = useContext(UserContext);
  const senderMongoUserId = user
    ? MongoUserIdSchema.fromString(user._id)
    : MongoUserIdSchema.makeNew();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  const saveDraft = useCallback(
    async (input: SaveDraftInput) => {
      setIsSavingDraft(true);
      try {
        if (existingDraftId) {
          const tx = collections.drafts.update(String(existingDraftId), (d) => {
            d.content = input.content;
            d.attachments = [...input.attachments];
          });
          await tx.isPersisted.promise;
        } else {
          const tempItem = buildTempDraftItem(
            conversationId,
            input.threadId,
            senderMongoUserId,
            input.inReplyToMessageId,
            {
              content: input.content,
              attachments: [...input.attachments],
              channel: input.channel,
              channelData: input.channelData,
            },
          );
          const tx = collections.drafts.insert(tempItem, { optimistic: false });
          await tx.isPersisted.promise;
        }
      } finally {
        setIsSavingDraft(false);
      }
    },
    [collections.drafts, conversationId, existingDraftId, senderMongoUserId],
  );

  /**
   * Ensures a draft exists and returns its server-assigned ID.
   * Used by useImageUpload to attach images to a draft before it's been saved.
   */
  const ensureDraftId = useCallback(async (): Promise<DraftMessageId> => {
    if (existingDraftId) return existingDraftId;

    const tempItem = buildTempDraftItem(
      conversationId,
      threadId,
      senderMongoUserId,
      inReplyToMessageId,
      {},
    );
    const tx = collections.drafts.insert(tempItem, { optimistic: false });
    // onInsert makes the API call; wrappedOnInsert then calls refetch().
    // By the time isPersisted resolves, the collection has real server data.
    await tx.isPersisted.promise;

    const found = [...collections.drafts.values()].find(
      (d) =>
        String(d.threadId) === String(threadId) &&
        (inReplyToMessageId
          ? d.inReplyToMessageId === String(inReplyToMessageId)
          : !d.inReplyToMessageId),
    );
    if (!found) throw new Error("Draft not found after creation");
    return found.id;
  }, [
    collections.drafts,
    conversationId,
    existingDraftId,
    senderMongoUserId,
    threadId,
    inReplyToMessageId,
  ]);

  const deleteDraft = useCallback(
    async (draftId: DraftMessageId) => {
      setIsDeletingDraft(true);
      try {
        const tx = collections.drafts.delete(String(draftId));
        await tx.isPersisted.promise;
        toast("Draft deleted", { variant: "success" });
      } catch {
        toast("Failed to delete draft", { variant: "error" });
      } finally {
        setIsDeletingDraft(false);
      }
    },
    [collections.drafts],
  );

  return {
    saveDraft,
    isSavingDraft,
    ensureDraftId,
    deleteDraft,
    isDeletingDraft,
  };
}
